import type { CommunityPurpose } from "./communityParticipation";
import type { CommunityPostListItem } from "./api";
import {
  listCommunityPostsFromSupabase,
  type ListCommunityPostsParams,
} from "./supabaseCommunity";
import {
  listInformateVibeSitesFromSupabase,
  type InformateVibeSiteItem,
} from "./supabaseInformateVibeSites";

export type CommunityBoardFeedItem =
  | { kind: "post"; id: number; created_at: string; post: CommunityPostListItem }
  | { kind: "vibe_site"; id: number; created_at: string; site: InformateVibeSiteItem };

export type CommunityBoardFeedSort = "recent" | "popular" | "hits" | "likes";

export type CommunityBoardFeedParams = {
  news?: "exclude" | "only";
  purpose?: CommunityPurpose | "";
  unanswered?: boolean;
  limit?: number;
  category_id?: number | "";
  subcategory_id?: number | "";
  sort?: CommunityBoardFeedSort;
  q?: string;
  tag?: string;
  author?: number;
};

/** Opaque cursor for infinite scroll; do not mutate outside this module. */
export type CommunityBoardFeedCursor =
  | {
      mode: "posts";
      nextPage: number;
    }
  | {
      mode: "mixed";
      postPage: number;
      sitePage: number;
      postBuffer: CommunityBoardFeedItem[];
      siteBuffer: CommunityBoardFeedItem[];
      postExhausted: boolean;
      siteExhausted: boolean;
    };

export type CommunityBoardFeedResult = {
  results: CommunityBoardFeedItem[];
  hasMore: boolean;
  cursor: CommunityBoardFeedCursor | null;
};

function wrapPost(post: CommunityPostListItem): CommunityBoardFeedItem {
  return { kind: "post", id: post.id, created_at: post.created_at, post };
}

function wrapSite(site: InformateVibeSiteItem): CommunityBoardFeedItem {
  return { kind: "vibe_site", id: site.id, created_at: site.created_at, site };
}

export function shouldMixVibeSites(params: CommunityBoardFeedParams): boolean {
  if (params.news === "only") return false;
  if ((params.purpose && params.purpose !== "showcase") || params.unanswered) return false;
  if (typeof params.category_id === "number") return false;
  if (typeof params.subcategory_id === "number") return false;
  if (params.tag && params.tag.trim()) return false;
  if (params.author) return false;
  return true;
}

function likesScore(item: CommunityBoardFeedItem): number {
  return item.kind === "post" ? item.post.num_likes : item.site.likes_count;
}

/** Prefer A over B for descending merge. */
function preferA(a: CommunityBoardFeedItem, b: CommunityBoardFeedItem, sort: CommunityBoardFeedSort): boolean {
  const aPinned = a.kind === "post" && a.post.top_fixed;
  const bPinned = b.kind === "post" && b.post.top_fixed;
  if (sort !== "recent" && aPinned !== bPinned) return aPinned;

  if (sort === "popular" || sort === "likes") {
    const sa = likesScore(a);
    const sb = likesScore(b);
    if (sa !== sb) return sa > sb;
  }

  if (sort === "hits") {
    const sa = a.kind === "post" ? a.post.hits : a.site.hits;
    const sb = b.kind === "post" ? b.post.hits : b.site.hits;
    if (sa !== sb) return sa > sb;
  }

  const ta = new Date(a.created_at).getTime();
  const tb = new Date(b.created_at).getTime();
  if (ta !== tb) return ta > tb;
  // Stable tie-break: posts before sites, then higher id
  if (a.kind !== b.kind) return a.kind === "post";
  return a.id >= b.id;
}

async function fetchPostPage(
  params: CommunityBoardFeedParams,
  page: number,
  limit: number
): Promise<{ items: CommunityBoardFeedItem[]; exhausted: boolean }> {
  const postParams: ListCommunityPostsParams = {
    page,
    limit,
    news: params.news,
    purpose: params.purpose,
    unanswered: params.unanswered,
    category_id: params.category_id,
    subcategory_id: params.subcategory_id,
    sort: params.sort,
    chronologicalLatest: true,
    q: params.q,
    tag: params.tag,
    author: params.author,
  };
  const res = await listCommunityPostsFromSupabase(postParams);
  if (!res) throw new Error("게시글을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.");
  return {
    items: res.results.map(wrapPost),
    exhausted: !res.next,
  };
}

async function fetchSitePage(
  params: CommunityBoardFeedParams,
  page: number,
  limit: number
): Promise<{ items: CommunityBoardFeedItem[]; exhausted: boolean }> {
  try {
    const siteSort = params.sort === "popular" || params.sort === "likes" ? "popular" : params.sort === "hits" ? "hits" : "latest";
    const res = await listInformateVibeSitesFromSupabase({
      page,
      limit,
      sort: siteSort,
      search: params.q?.trim() || undefined,
    });
    return {
      items: res.results.map(wrapSite),
      exhausted: !res.next,
    };
  } catch (e) {
    console.error("Vibe sites feed fetch failed; continuing with posts only.", e);
    return { items: [], exhausted: true };
  }
}

async function listPostsOnly(
  params: CommunityBoardFeedParams,
  limit: number,
  cursor: Extract<CommunityBoardFeedCursor, { mode: "posts" }> | null
): Promise<CommunityBoardFeedResult> {
  const page = cursor?.nextPage ?? 1;
  const { items, exhausted } = await fetchPostPage(params, page, limit);
  return {
    results: items,
    hasMore: !exhausted,
    cursor: exhausted ? null : { mode: "posts", nextPage: page + 1 },
  };
}

async function refillMixedBuffers(
  params: CommunityBoardFeedParams,
  limit: number,
  state: Extract<CommunityBoardFeedCursor, { mode: "mixed" }>
): Promise<void> {
  const tasks: Promise<void>[] = [];

  if (state.postBuffer.length === 0 && !state.postExhausted) {
    tasks.push(
      (async () => {
        const { items, exhausted } = await fetchPostPage(params, state.postPage, limit);
        state.postBuffer.push(...items);
        state.postExhausted = exhausted || items.length === 0;
        if (items.length > 0) state.postPage += 1;
        else state.postExhausted = true;
      })()
    );
  }

  if (state.siteBuffer.length === 0 && !state.siteExhausted) {
    tasks.push(
      (async () => {
        const { items, exhausted } = await fetchSitePage(params, state.sitePage, limit);
        state.siteBuffer.push(...items);
        state.siteExhausted = exhausted || items.length === 0;
        if (items.length > 0) state.sitePage += 1;
        else state.siteExhausted = true;
      })()
    );
  }

  if (tasks.length) await Promise.all(tasks);
}

async function listMixed(
  params: CommunityBoardFeedParams,
  limit: number,
  cursor: Extract<CommunityBoardFeedCursor, { mode: "mixed" }> | null
): Promise<CommunityBoardFeedResult> {
  const state: Extract<CommunityBoardFeedCursor, { mode: "mixed" }> = cursor
    ? {
        mode: "mixed",
        postPage: cursor.postPage,
        sitePage: cursor.sitePage,
        postBuffer: [...cursor.postBuffer],
        siteBuffer: [...cursor.siteBuffer],
        postExhausted: cursor.postExhausted,
        siteExhausted: cursor.siteExhausted,
      }
    : {
        mode: "mixed",
        postPage: 1,
        sitePage: 1,
        postBuffer: [],
        siteBuffer: [],
        postExhausted: false,
        siteExhausted: false,
      };

  const sort = params.sort ?? "recent";
  const results: CommunityBoardFeedItem[] = [];

  while (results.length < limit) {
    await refillMixedBuffers(params, limit, state);

    const headPost = state.postBuffer[0];
    const headSite = state.siteBuffer[0];

    if (!headPost && !headSite) break;

    let next: CommunityBoardFeedItem;
    if (headPost && headSite) {
      next = preferA(headPost, headSite, sort) ? state.postBuffer.shift()! : state.siteBuffer.shift()!;
    } else if (headPost) {
      next = state.postBuffer.shift()!;
    } else {
      next = state.siteBuffer.shift()!;
    }
    results.push(next);
  }

  const hasMore =
    state.postBuffer.length > 0 ||
    state.siteBuffer.length > 0 ||
    !state.postExhausted ||
    !state.siteExhausted;

  return {
    results,
    hasMore,
    cursor: hasMore ? state : null,
  };
}

/**
 * Community board feed: posts only, or posts + published vibe sites merged client-side.
 */
export async function listCommunityBoardFeed(
  params: CommunityBoardFeedParams,
  cursor: CommunityBoardFeedCursor | null = null
): Promise<CommunityBoardFeedResult> {
  const limit = Math.min(params.limit ?? 20, 60);

  if (!shouldMixVibeSites(params)) {
    const postsCursor = cursor?.mode === "posts" ? cursor : null;
    return listPostsOnly(params, limit, postsCursor);
  }

  const mixedCursor = cursor?.mode === "mixed" ? cursor : null;
  return listMixed(params, limit, mixedCursor);
}
